import { useMemo } from 'react';
import { pick } from 'ramda';

import { usePortletConfig } from '@core/js/components/system/Portlet';

import {
    useGetPinnedGames
} from '@interlayer/js/app/components/main/Games/components/GameCategory/components/GameList/hooks/useGetPinnedGames';
import { useFilterFeedBlacklist } from '@interlayer/js/components/system/App/hooks/useGetGamesConfiguration/helpers';

import {
    useIsCategoryLoading
} from 'js/app/components/main/Games/components/GameCategory/components/GameList/hooks/useIsCategoryLoading';
import { DYNAMIC_CATEGORIES } from 'js/app/components/main/Games/constants';
import { useCustomCategory } from 'js/app/components/main/Games/hooks/useCustomCategory';
import { useGameComponentConfig } from 'js/app/components/main/Games/hooks/useGameComponentConfig';
import { useGamesSelector } from 'js/app/components/main/Games/store/context';
import { useRecentlyGamesSelector } from 'js/app/store/hooks/useRecentlyGames';
import { useAppSelector, useUserSelector } from 'js/store/hooks';

import { gamesSortingByBingoSchedule } from './useGetBingoSchedule/helpers';
import { useGameFilter } from './useGameFilter';
import { useGameSorting } from './useGameSorting';
import { useGetBingoSchedule } from './useGetBingoSchedule';
import { useJackpotFilter } from './useJackpotFilter';

export const useGameList = (category) => {
    const { recentlyPlayedGames } = useRecentlyGamesSelector();
    const {
        gamesConfiguration,
        favoriteGames,
        gamesFeeds
    } = useAppSelector(pick(['gamesConfiguration', 'favoriteGames', 'gamesFeeds']));
    const { isLoggedIn, futureAnthemsList } = useUserSelector(pick(['isLoggedIn', 'futureAnthemsList']));
    const {
        pggGameList,
        sorting,
        jackpotOnly
    } = useGamesSelector();

    const filterFeedBlacklist = useFilterFeedBlacklist();
    const getPinnedGames = useGetPinnedGames();
    const { viewMode } = useGameComponentConfig();
    const viewModeInline = viewMode === 'inline';
    const checkIsCategoryLoading = useIsCategoryLoading();
    const isLoading = checkIsCategoryLoading(category);
    const { gamesConfiguration: { lobbyName } = {} } = usePortletConfig();
    const {
        state: {
            data: bingoScheduleData,
            isLoaded: isLoadedBingoSchedule
        }
    } = useGetBingoSchedule({ lobbyName });

    const {
        gameCountLimit,
        usePositions,
        positions1x2,
        positions2x1,
        positions2x2,
        rowsInAllCat,
        futureAnthemsEnabled,
        futureAnthemsRecommendationType = 'recommendations'
    } = useCustomCategory(category.name);

    const filterGameFunction = useGameFilter();
    const jackpotFilter = useJackpotFilter();
    const gameListSortingFunc = useGameSorting(sorting);

    const isFutureAnthemsEnabled = useMemo(() => futureAnthemsEnabled && isLoggedIn, [futureAnthemsEnabled, isLoggedIn]);
    const {
        gamesList: futureAnthemsEnabledGameList = []
    } = futureAnthemsList[futureAnthemsRecommendationType] || {};

    const isPggEnabled = category.pgg2?.enabled;
    const pggEnabledGameList = useMemo(() => {
        return pggGameList?.[category.name]?.pggGameList || [];
    }, [category.name, pggGameList]);

    const isDynamicCategory = useMemo(() => {
        return category.name === DYNAMIC_CATEGORIES.RECENTLY
            || category.name === DYNAMIC_CATEGORIES.FAVOURITES
            || isFutureAnthemsEnabled && futureAnthemsEnabledGameList.length
            || isPggEnabled && pggEnabledGameList.length;
    }, [category.name, futureAnthemsEnabledGameList.length, isFutureAnthemsEnabled, isPggEnabled, pggEnabledGameList.length]);

    return useMemo(() => {
        if (isLoading || Boolean(lobbyName) && !isLoadedBingoSchedule) {
            return [];
        }

        // INITIAL GAME CODES
        let gameCodes;
        if (category.name === DYNAMIC_CATEGORIES.RECENTLY) {
            gameCodes = recentlyPlayedGames || [];
        } else if (category.name === DYNAMIC_CATEGORIES.FAVOURITES) {
            gameCodes = favoriteGames || [];
        } else if (isFutureAnthemsEnabled && futureAnthemsEnabledGameList.length) {
            gameCodes = futureAnthemsEnabledGameList;
        } else if (isPggEnabled && pggEnabledGameList.length) {
            gameCodes = pggEnabledGameList;
        } else {
            gameCodes = category.gameCodes || [];
        }

        let games = filterFeedBlacklist(gameCodes, gamesFeeds)
            .map(gameCode => ({ ...gamesConfiguration[gameCode], viewMode: 'detailed' }))
            .filter(filterGameFunction);

        if (lobbyName) {
            games = gamesSortingByBingoSchedule(bingoScheduleData, games);

            if (!isDynamicCategory) {
                games = getPinnedGames(games);
            }
        } else if (!isDynamicCategory) {
            games = getPinnedGames(games);
        }

        // USER JACKPOT FILTERS
        if (jackpotOnly) {
            games = games.filter(jackpotFilter);
        }

        // GAME LIMIT COUNT
        if (gameCountLimit) {
            games = games.slice(0, gameCountLimit);
        }

        // SORTING
        if (sorting !== '1') {
            games = games.sort(gameListSortingFunc);
        }

        // TILING PARAMS
        if (games.length) {
            if ((!viewModeInline || rowsInAllCat !== 1) && usePositions) {
                positions1x2.split(',').forEach(index => {
                    if (index && games[parseInt(index) - 1]) {
                        games[parseInt(index) - 1].tileSize = '1x2';
                    }
                });

                positions2x1.split(',').forEach(index => {
                    if (index && games[parseInt(index) - 1]) {
                        games[parseInt(index) - 1].tileSize = '2x1';
                    }
                });

                positions2x2.split(',').forEach(index => {
                    if (index && games[parseInt(index) - 1]) {
                        games[parseInt(index) - 1].tileSize = '2x2';
                    }
                });
            }
        }

        return games;
    }, [
        category.gameCodes, category.name, favoriteGames, filterGameFunction, gameCountLimit,
        gameListSortingFunc, gamesConfiguration, isPggEnabled, jackpotFilter, jackpotOnly, pggEnabledGameList, sorting,
        positions1x2, positions2x1, positions2x2, recentlyPlayedGames, rowsInAllCat, usePositions, viewModeInline,
        futureAnthemsEnabledGameList, isFutureAnthemsEnabled, isLoading, lobbyName, isLoadedBingoSchedule, bingoScheduleData,
        getPinnedGames, isDynamicCategory, gamesFeeds, filterFeedBlacklist
    ]);
};
